import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import { Container, Spacer, Text, Button, ButtonBar, Icon, RadialButton, Form, hooks, Box } from 'roadie-ui'
import { ButtonBarButtonProps } from 'roadie-ui/dist/dst/ui/ButtonBar'
import { vehicleTypes, vehicleColors, vehicleSubTypes, VehicleType, VehicleCategory, getVehicleHex } from 'util/vehicle'
import { useLocalization, LocalizedText } from 'localization'

export const vehicles: {
  name: string
  value: VehicleCategory
}[] = [
  {
    name: 'Two Door / Sedan',
    value: 'two_door',
  },
  {
    name: 'Hatchback / SUV',
    value: 'hatchback_suv',
  },
  {
    name: 'Minivan / Passenger Van',
    value: 'van',
  },
  {
    name: 'Pickup Truck',
    value: 'pickup_truck',
  },
  {
    name: 'Cargo Van',
    value: 'cargo_van',
  },
  {
    name: 'Box Truck (10 - 15 ft)',
    value: 'box_truck',
  },
  {
    name: 'Box Truck (16 - 25 ft)',
    value: 'large_box_truck',
  },
  {
    name: 'Box Truck (26+ ft)',
    value: 'oversized_box_truck',
  },
]

// Keep color options in case we go back to using colors for the Vehicle Color Select
const ColorPreview = styled.div<{ color: string }>`
  & > * {
    background-color: ${(props) => props.color};
  }
`

const Color: React.FC<ButtonBarButtonProps> = (props) => {
  return (
    <Button.Secondary
      type="button"
      $textTransform="initial"
      $backgroundColor="neutral-lightest"
      $color="neutral-black"
      $borderRadius="10rem"
      $borderColor={props.selected ? 'primary-base' : 'clear'}
      $borderWidth="2px"
      $borderStyle="solid"
      onClick={props.onClick}
    >
      <Container.Grid
        $gridTemplateColumns="2.4rem 1fr"
        $gridGap="1rem"
        $alignItems="center"
        $textAlign="left"
        $minWidth="8rem"
      >
        <ColorPreview color={getVehicleHex(props.option.value)}>
          <Box.Model
            $height="2.4rem"
            $width="2.4rem"
            $borderRadius="4px"
            $borderWidth="1px"
            $borderStyle="solid"
            $borderColor="neutral-base"
          />
        </ColorPreview>
        <Text.Caption>{props.option.label}</Text.Caption>
      </Container.Grid>
    </Button.Secondary>
  )
}

const ColorSelector = styled.div`
  & > * {
    margin: auto;
  }

  & > * > * {
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 1rem;
  }
`

type VehicleColorSelectBubbleProps = {
  name?: string
  color?: string
}

export const VehicleColorSelectBubble: React.FC<VehicleColorSelectBubbleProps> = (props) => {
  const { t } = useLocalization()
  const { name, color: defaultColor = '' } = props
  const [color, setColor] = React.useState(defaultColor)
  const [expanded, setExpanded] = useState(false)
  const handleViewAll = useCallback(() => {
    setExpanded(true)
  }, [])
  const handleSelect = useCallback((newSelected: string) => {
    setColor(newSelected)
  }, [])
  const colors = expanded ? vehicleColors : vehicleColors.slice(0, 6)
  return (
    <Container.Grid $justifyItems="center" $gridGap="1rem">
      <Form.Input type="hidden" name={name || 'color'} value={color} />
      <ColorSelector>
        <ButtonBar
          selectedValue={color}
          onChange={handleSelect}
          containerColor="clear"
          buttonComp={Color}
          buttons={colors.map((color) => ({
            label: t(color.labelKey),
            value: color.value,
          }))}
        />
      </ColorSelector>
      {!expanded && (
        <Button.Tertiary type="button" onClick={handleViewAll} $textTransform="initial" $letterSpacing="0">
          <LocalizedText translationKey="dweb_form_vehicle_select_view_more_colors" />
        </Button.Tertiary>
      )}
    </Container.Grid>
  )
}

type VehicleColorSelectProps = {
  defaultValues?: {
    color?: string
  }
}

export const VehicleColorSelect: React.FC<VehicleColorSelectProps> = ({ defaultValues }) => {
  const { t } = useLocalization()
  const [color, setColor] = useState(defaultValues?.color || '')

  const handleSelect = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setColor(e.target.value)
  }, [])

  return (
    <Form.Select
      id="color"
      label={t('dweb_form_vehicle_select_select_color')}
      name={'color'}
      value={color}
      onChange={handleSelect}
      data-cy="vehicle-color-select"
    >
      {vehicleColors.map((c) => (
        <option key={c.value} value={c.value}>
          {t(c.labelKey)}
        </option>
      ))}
    </Form.Select>
  )
}

const RadioButton: React.FC<React.PropsWithChildren<ButtonBarButtonProps>> = (props) => {
  return (
    <Button.Tertiary
      type="button"
      $textTransform="initial"
      $textAlign="left"
      $color="neutral-black"
      $width="100%"
      onClick={props.onClick}
    >
      <Container.Grid $gridTemplateColumns="3rem 1fr" $gridGap="1rem" $padding="1rem" $alignItems="center">
        <RadialButton checked={props.selected} />
        {props.children}
      </Container.Grid>
    </Button.Tertiary>
  )
}

const SubCategory = (category: VehicleType): React.FC<ButtonBarButtonProps> => {
  const SubCategoryButton: React.FC<ButtonBarButtonProps> = (props) => {
    const { t } = useLocalization()
    const subType = vehicleSubTypes[category].find((v) => v.name === props.option.label)

    if (!subType) {
      return <></>
    }

    return (
      <RadioButton {...props}>
        <div>
          <Text.Body2>{t(subType.labelKey)}</Text.Body2>
          <Text.Caption>{t(subType.descriptionKey)}</Text.Caption>
        </div>
      </RadioButton>
    )
  }

  return SubCategoryButton
}

const VerticalButtonBar = styled.div`
  & > * {
    width: 100%;
  }

  & > * > * {
    width: 100%;
    grid-template-columns: 1fr;
  }
`

type SubcategoriesProps = {
  category?: VehicleType
  subcategory?: string
  onChange: (value: string) => void
}

const Subcategories: React.FC<SubcategoriesProps> = (props) => {
  const { category, subcategory, onChange } = props

  if (!category) {
    return <></>
  }

  return (
    <VerticalButtonBar>
      <ButtonBar
        key={`${category}-${subcategory}`}
        selectedValue={subcategory}
        onChange={onChange}
        containerColor="clear"
        buttonComp={SubCategory(category)}
        buttons={vehicleSubTypes[category].map((subType) => ({
          label: subType.name,
          value: subType.name,
        }))}
      />
    </VerticalButtonBar>
  )
}

const VehicleTypesLayout = styled.div<{ maxWidth?: string }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin: auto;
  max-width: ${(props) => props.maxWidth};
`

type VehicleTypeSelectProps = {
  categoryName?: string
  subcategoryName?: string
  category?: VehicleType
  subcategory?: string
  enclosed?: boolean
}

export const VehicleTypeSelect: React.FC<VehicleTypeSelectProps> = (props) => {
  const { t } = useLocalization()
  const { categoryName, subcategoryName, category: defaultCategory, subcategory: defaultSubcategory } = props
  const [category, setCategory] = React.useState(defaultCategory)
  const [subcategory, setSubcategory] = React.useState(defaultSubcategory)
  const handleCategoryChange = React.useCallback((value: VehicleType) => {
    setCategory(value)
    setSubcategory('')
  }, [])
  const handleSubcategoryChange = React.useCallback((value: string) => {
    setSubcategory(value)
  }, [])
  const maxWidth = hooks.useBreakPoints({ mobile: '25rem', tablet: '100%', desktop: '100%' })

  return (
    <>
      <Form.Input type="hidden" name={categoryName || 'category'} value={category} />
      <Form.Input type="hidden" name={subcategoryName || 'subcategory'} value={subcategory} />
      <VehicleTypesLayout maxWidth={maxWidth}>
        {vehicleTypes.map((v) => (
          <Button.Secondary
            key={v.type}
            type="button"
            onClick={() => handleCategoryChange(v.type)}
            $textTransform="initial"
            $width="7.2rem"
            $height="7.2rem"
            $backgroundColor="neutral-lightest"
            $color="neutral-black"
            $borderRadius="50%"
            $borderColor={v.type === category ? 'primary-base' : 'clear'}
            $borderWidth="2px"
            $borderStyle="solid"
          >
            <Icon svg={v.icon} $color="primary-base" $width="2.5rem" />
            <Text.Caption>{t(v.labelKey)}</Text.Caption>
          </Button.Secondary>
        ))}
      </VehicleTypesLayout>
      <Spacer $height="1rem" />
      <Subcategories category={category} subcategory={subcategory} onChange={handleSubcategoryChange} />
    </>
  )
}
